import { RequestStatusRenderer } from "components/requestStatsRenderer/RequestStatusRenderer";
import CardReporting from "components/cards/CardReporting/CardReporting";
import {
  type PerformanceWithAccountFilter,
  type PerformanceFilter,
} from "features/Library/Common/utils/performanceUtils";
import { type LicensesAccountPackRow, type PackIdAndName } from "./types";
import { useLicensesEngagementQuery, createLicensesAccountPacksFetcher } from "./Queries/queries";
import {
  GenericPerformanceListWithServerPagination,
  type ExportAvailability,
} from "features/Reporting/Content/shared/GenericPerformanceListWithServerPagination";
import styles from "./packsReportCustomerDrilldown.module.scss";
import { connect, type ConnectedProps } from "react-redux";
import { bindActionCreators } from "@reduxjs/toolkit";
import { bindAction } from "interfaces";
import { setHasDataToExport, setExportAction } from "features/Reporting/state/export/exportSlice";
import { useMemo, useCallback, useEffect } from "react";
import { TextTruncate } from "components";
import { Tooltip } from "components/common/tooltip";
import utils from "../../../../utils/miscellaneousUtils";
import dateTimeUtils from "../../../../utils/dateTimeUtils";
import { type ColumnOption } from "interfaces/columnOptions";
import { doLicensesAccountPacksExport } from "./Queries/doLicensesAccountPacksExport";
import PackPurchasedPerformance from "../../../Licensing/Packs/PurchasedView/Performance/PackPurchasedPerformance";
import { type AppDispatch } from "features/Application/globaltypes/redux";

type Props = PropsFromRedux & {
  filter: PerformanceWithAccountFilter;
  setSelectedPack: (pack: PackIdAndName | null) => void;
  selectedPack: PackIdAndName | null;
  selectedAccount: { id: number; name: string };
};

const PacksReportCustomerDrilldown = ({
  filter,
  setHasExportData,
  setExportAction,
  licensesAccountPacksExport,
  setSelectedPack,
  selectedPack,
  selectedAccount,
}: Props) => {
  const engagementQuery = useLicensesEngagementQuery(filter);

  useEffect(() => {
    setExportAction({
      method: licensesAccountPacksExport,
      args: [filter, `${selectedAccount.name} Packs Export`],
      isExportEnabled: true,
    });
  }, [filter, licensesAccountPacksExport, setExportAction, selectedAccount.name]);

  const fetchAccountPacksPage = useMemo(() => createLicensesAccountPacksFetcher(filter), [filter]);

  const queryKeyBase = ["LicensesAccountPacks", filter] as const;

  const columns = useMemo<ColumnOption<LicensesAccountPackRow>[]>(
    () => [
      {
        name: "Pack Name",
        width: 3,
        isSortable: true,
        sortKey: "PackName",
        render: (item) => (
          <Tooltip
            showAlways
            target={
              <button
                className={styles.linkButton}
                onClick={() => setSelectedPack({ packId: item.PackId, packName: item.PackName })}
              >
                <TextTruncate className={styles.wordBreak}>{item.PackName}</TextTruncate>
              </button>
            }
            content={item.PackName}
          />
        ),
      },
      {
        name: "Vendor",
        width: 2,
        isSortable: true,
        sortKey: "Vendor",
        render: (item) => (
          <Tooltip
            target={<TextTruncate className={styles.wordBreak}>{item.Vendor}</TextTruncate>}
            content={item.Vendor}
          />
        ),
      },
      {
        name: "Type",
        width: 2,
        isSortable: true,
        sortKey: "PackType",
        render: (item) => item.PackType,
      },
      {
        name: "Status",
        width: 2,
        isSortable: true,
        sortKey: "PackStatus",
        render: (item) => item.PackStatus,
      },
      {
        name: "Issued Licenses",
        width: 3,
        isSortable: true,
        sortKey: "IssuedLicenses",
        render: (item) => item.IssuedLicenses.toLocaleString(),
      },
      {
        name: "Contracted Licenses",
        width: 4,
        isSortable: true,
        sortKey: "ContractedLicenses",
        render: (item) => {
          let value;
          if (item.IsAutoIncrement) {
            value = "Auto-Increment";
          } else if (item.IsUnlimited) {
            value = "Unlimited";
          } else {
            value = utils.unlimitedNumber(item.ContractedLicenses);
          }
          return <Tooltip target={<TextTruncate className={styles.wordBreak}>{value}</TextTruncate>} content={value} />;
        },
      },
      {
        name: "Date Enabled",
        width: 3,
        isSortable: true,
        sortKey: "DateEnabled",
        render: (item) => dateTimeUtils.formatEmptyDate(item.DateEnabled),
      },
      {
        name: "Expiration Date",
        width: 3,
        isSortable: true,
        sortKey: "ExpirationDate",
        render: (item) => (item.ExpirationDate === null ? "-" : dateTimeUtils.formatEmptyDate(item.ExpirationDate)),
      },
    ],
    [setSelectedPack],
  );

  const updateExportDataState = useCallback(
    ({ hasDataToExport }: ExportAvailability) =>
      setHasExportData({
        hasDataToExport,
        tooltipContent: "No data available for export. This account packs report contains no data.",
      }),
    [setHasExportData],
  );

  const PackPurchasedPerformanceFilter = useMemo<PerformanceFilter>(
    () => ({ dateFrom: filter.dateFrom, dateTo: filter.dateTo }),
    [filter.dateFrom, filter.dateTo],
  );

  return (
    <div className={styles.root}>
      {selectedPack ? (
        <PackPurchasedPerformance
          filter={PackPurchasedPerformanceFilter}
          packId={selectedPack.packId}
          moboId={selectedAccount.id}
        />
      ) : (
        <>
          <div className={styles.cards}>
            <RequestStatusRenderer state={engagementQuery.status}>
              {engagementQuery.isSuccess && engagementQuery.data && (
                <CardReporting items={engagementQuery.data.licensesCards} />
              )}
            </RequestStatusRenderer>
          </div>
          <div className={styles.cardsSection}>
            <GenericPerformanceListWithServerPagination
              title="Packs"
              columns={columns}
              filter={filter}
              fetchPage={fetchAccountPacksPage}
              queryKeyBase={queryKeyBase}
              hasDataForExport={updateExportDataState}
              isDateAllTime
            />
          </div>
        </>
      )}
    </div>
  );
};

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  setHasExportData: bindAction(setHasDataToExport, dispatch),
  setExportAction: bindAction(setExportAction, dispatch),
  licensesAccountPacksExport: bindActionCreators(doLicensesAccountPacksExport, dispatch),
});

const connector = connect(null, mapDispatchToProps);
export type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(PacksReportCustomerDrilldown);
export default ConnectedComponent;
